import './DevPanel_ApolloStateSplitPane.scss';

import React, { useCallback, useEffect, useState } from 'react';

import { Alert, Box, CircularProgress, Tab, Tabs, Typography } from '@mui/material';
import { SplitPane } from '@rexxars/react-split-pane';
import PropTypes from 'prop-types';

import { useAppConfigContext } from '@ulta/core/providers/AppConfigProvider/AppConfigProvider';

import { useScheduledContent } from '../../hooks/useScheduledContent/useScheduledContent';
import { DevPanel_SocketProvider } from '../../providers/DevPanel_SocketProvider/DevPanel_SocketProvider';
import DevPanel_AmpliencePanel from '../DevPanel_AmpliencePanel/DevPanel_AmpliencePanel';
import DevPanel_AmplienceUsagePanel from '../DevPanel_AmplienceUsagePanel/DevPanel_AmplienceUsagePanel';
import DevPanel_ApolloStateTreeView from '../DevPanel_ApolloStateTreeView/DevPanel_ApolloStateTreeView';
import DevPanel_DxlTreeView from '../DevPanel_DxlTreeView/DevPanel_DxlTreeView';
import DevPanel_SchedulingTab from '../DevPanel_SchedulingTab/DevPanel_SchedulingTab';
import DevPanel_TabPanel from '../DevPanel_TabPanel/DevPanel_TabPanel';


const ErrorPanel = ( { message } ) => (
  <Box p={ 3 }>
    <Alert severity='error'>{ message }</Alert>
  </Box>
);

ErrorPanel.propTypes = {
  message: PropTypes.string.isRequired
};

/**
 * Main split pane layout for Apollo state debugging.
 */
const DevPanel_ApolloStateSplitPane = () => {
  const [tabIndex, setTabIndex] = useState( 0 );
  const [apolloState, setApolloState] = useState( null );
  const [selectedNode, setSelectedNode] = useState( null );

  const { serverRouteUtility } = useAppConfigContext();

  // ---- Apollo state init (test-safe) ----
  useEffect( () => {
    if( typeof window !== 'undefined' && window.__APOLLO_STATE__ ){
      setApolloState( window.__APOLLO_STATE__ );
    }
  }, [] );

  const {
    isLoading: loadingScheduled,
    error: scheduledError,
    activeScheduledData,
    windowWeeks
  } = useScheduledContent( serverRouteUtility, selectedNode, apolloState );

  const handleTabChange = useCallback( ( _, newValue ) => {
    setTabIndex( newValue );
  }, [] );

  if( !apolloState ){
    return (
      <Box p={ 3 }>
        <Typography variant='body2' color='textSecondary'>
          Apollo State not found on this page.
        </Typography>
      </Box>
    );
  }

  return (
    <DevPanel_SocketProvider>
      <div className='DevPanel_ApolloStateSplitPane'>
        <SplitPane split='vertical' minSize='45%'>

          { /* LEFT PANE */ }
          <Box className='pane-content tree-container'>
            <DevPanel_ApolloStateTreeView
              apolloState={ apolloState }
              setSelectedNode={ setSelectedNode }
            />
          </Box>

          { /* RIGHT PANE */ }
          <Box className='pane-content right-pane'>
            { selectedNode ? (
              <>
                <Tabs
                  value={ tabIndex }
                  onChange={ handleTabChange }
                  variant='fullWidth'
                  className='tabs-container'
                >
                  <Tab label='Details' id='tab-0' />
                  <Tab label='Usage' id='tab-1' />
                  <Tab label='Scheduling' id='tab-2' />
                  <Tab label='Data' id='tab-3' />
                </Tabs>

                <DevPanel_TabPanel value={ tabIndex } index={ 0 }>
                  <DevPanel_AmpliencePanel module={ selectedNode } />
                </DevPanel_TabPanel>

                <DevPanel_TabPanel value={ tabIndex } index={ 1 }>
                  <DevPanel_AmplienceUsagePanel module={ selectedNode } />
                </DevPanel_TabPanel>

                <DevPanel_TabPanel value={ tabIndex } index={ 2 }>
                  { scheduledError ? (
                    <ErrorPanel message={ `Scheduling error: ${scheduledError.message || scheduledError}` } />
                  ) : (
                    <DevPanel_SchedulingTab
                      isLoading={ loadingScheduled }
                      error={ scheduledError }
                      activeScheduledData={ activeScheduledData }
                      windowWeeks={ windowWeeks }
                    />
                  ) }
                </DevPanel_TabPanel>

                <DevPanel_TabPanel value={ tabIndex } index={ 3 }>
                  <DevPanel_DxlTreeView ApolloState={ selectedNode } />
                </DevPanel_TabPanel>
              </>
            ) : (
              <Box className='empty-state' p={ 4 }>
                <Typography variant='body2' align='center' color='textSecondary'>
                  Select a module from the tree to view its detailed scheduling and metadata.
                </Typography>
              </Box>
            ) }
          </Box>
        </SplitPane>
      </div>
    </DevPanel_SocketProvider>
  );
};

export default DevPanel_ApolloStateSplitPane;
